import { THREE } from "../config/ThreeConfig.js";
import { getArtModeOverride, getCharacterArt, isArtEnabled } from "../assets/CharacterArt.js";
import { getConfiguredGLTFLoader } from "../utils/gltfLoader.js";
import { isTestEnvironment } from "../utils/runtimeEnv.js";
/**
 * Base Player Class
 * Handles player movement, combat, and state
 */


export class Player {
  constructor(game, options = {}) {
    this.game = game;
    this.character = options.character || 'honeybadger';
    this.color = options.color || '#00c6ff';
    this.username = options.username || 'Mercenary';
    
    // Player state
    this.health = 100;
    this.maxHealth = 100;
    this.shields = 100;
    this.maxShields = 100;
    this.boost = 100;
    this.maxBoost = 100;
    this.lives = 3;
    this.score = 0;
    this.equity = 0;
    
    // Weapon state
    this.weaponLevel = 1; // 1=single, 2=twin, 3=hyper
    this.bombs = 5;
    this.maxBombs = 9;
    this.lastFireTime = 0;
    this.fireRate = 0.15; // seconds between shots
    this.damage = 10;

    // Special ability (per-character overrides can change these).
    this.specialName = null;
    this.specialButtonHint = "E / Y";
    this.specialCooldownMs = 0;
    this.specialDurationMs = 0;
    this.specialSummary = "";
    this.specialUsage = "";
    this.specialEffect = "";
    this.specialTacticalTip = "";
    this._specialReadyAtMs = 0;
    this._specialActiveUntilMs = 0;
    this._specialBriefingShown = false;
    
    // Movement state
    this.speed = 50;
    this.maxSpeed = 100;
    this.boostSpeed = 150;
    this.brakeSpeed = 20;
    this.rotationSpeed = 2;
    this.position = new THREE.Vector3(0, 0, 0);
    this.velocity = new THREE.Vector3(0, 0, 0);
    this.rotation = new THREE.Euler(0, 0, 0, 'YXZ');
    this.currentSpeed = this.speed;
    this.forwardVelocity = this.speed;

    // Arcade lane bounds (readable Star Fox-style movement freedom).
  this.laneBounds = {
    x: 180,
    y: 115,
  };

  this._baseLaneBounds = { x: 180, y: 115 };
    this.targetPosition = new THREE.Vector3(0, 0, 0);
    this._targetLane = new THREE.Vector2(0, 0);

    // Exposed for camera/FX tuning.
    this.isBoosting = false;
    this.isBraking = false;

    // On-rails control state (Star Fox style):
    // - stick input can be transformed by current bank
    // - yaw/pitch/roll are smoothed independently
    // - reticle projects from ship forward so shots stay truthful
    this.aim = new THREE.Vector2(0, 0);
    this._stickFiltered = new THREE.Vector2(0, 0);
    this._reticleStick = new THREE.Vector2(0, 0);
    this._bankAngle = 0;
    this._cameraLead = new THREE.Vector2(0, 0);
    this.reticleDistance = 640;

    // Star Fox-ish charge + lock-on.
    this._fireWasDown = false;
    this._fireDownAtMs = 0;
    this._isCharging = false;
    this._chargeStartAtMs = 0;
    this._chargeProgress = 0;
    this._lockTarget = null;
    this._lockStrength = 0;
    this._lockHeldMs = 0;
    
    // 3D object
    this.object3D = new THREE.Group();
    this.ship = null;
    this.shipActivated = null;
    this.shipActivatedPromise = null;
    this._activatedShipWanted = false;
    this.trail = null;
    this.collider = null;
    this.shieldMesh = null;
    this._shieldPulse = 0;
    this._specialLastState = null;
    this._shipVisualNodes = [];
    this._shipLoadToken = 0;
    this._disposed = false;
    this._engineGlow = null;
    
    // State flags
    this.isInvulnerable = false;
    this.invulnerableTime = 2;
    this.isBarrelRolling = false;
    this.barrelRollTime = 0.5;
    this.barrelRollTimer = 0;
    
    // Bind methods
    this.update = this.update.bind(this);
  }

  applyShipOpacity(opacity) {
    if (!this.ship) return;

    const applyToMaterial = (material) => {
      if (!material) return;
      if (Array.isArray(material)) {
        material.forEach(applyToMaterial);
        return;
      }
      if (typeof material !== "object") return;
      if (!("opacity" in material)) return;

      // Ensure opacity actually takes effect when flickering.
      if (opacity < 1) material.transparent = true;
      material.opacity = opacity;
      material.needsUpdate = true;
    };

    // Most ships are Meshes, but GLB ships are often Groups without a direct material.
    if (this.ship.material) {
      applyToMaterial(this.ship.material);
      if (this._activatedShipWanted && this.shipActivated?.visible && "visible" in this.ship) {
        this.ship.visible = false;
      }
      return;
    }

    let applied = false;
    this.ship.traverse?.((obj) => {
      if (!obj?.isMesh) return;
      if (!obj.material) return;
      applied = true;
      applyToMaterial(obj.material);
    });

    // Last resort: if we can’t reach any materials, at least avoid crashing.
    if (!applied && "visible" in this.ship) {
      this.ship.visible = opacity > 0.2;
    }
    if (this._activatedShipWanted && this.shipActivated?.visible && "visible" in this.ship) {
      this.ship.visible = false;
    }
  }
  
  async init() {
    this.ensureCollider();
    // Create ship based on character
    // Create a placeholder immediately; swap in the GLB asynchronously when ready.
    // This keeps gameplay responsive while still always using GLBs in-game.
    this.createShip().catch(() => {});
    this.ensureEngineGlow();
    this.ensureShieldMesh();

    // Best-effort: start loading ability assets early so pressing E/Y shows results quickly.
    // Do not block startup on large GLB ability payloads.
    try {
      const preloadResult = this.preloadAbilityAssets?.();
      if (preloadResult && typeof preloadResult.then === "function") {
        preloadResult.catch(() => {});
      }
    } catch {
      // ignore
    }
    
    // Setup trails
    this.createTrail();

    // Start aim tethered to the ship.
    this.aim.set(this.position.x, this.position.y);
    
    return Promise.resolve();
  }

  // Overridden by subclasses to begin loading ability GLBs/templates early.
  preloadAbilityAssets() {}

  ensureEngineGlow() {
    if (this._engineGlow) return this._engineGlow;
    // Retro readability: a small additive engine core helps the ship pop without touching GLB textures.
    const geom = new THREE.SphereGeometry(2.4, 10, 10);
    const mat = new THREE.MeshBasicMaterial({
      color: this.getCharacterColor(),
      transparent: true,
      opacity: 0.28,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      fog: false,
      toneMapped: false,
    });
    const mesh = new THREE.Mesh(geom, mat);
    mesh.name = "engine-glow";
    mesh.renderOrder = 10;
    mesh.frustumCulled = false;
    // Ship faces down -Z; engine glow sits behind (positive Z).
    mesh.position.set(0, 0.5, 18);
    this.object3D.add(mesh);
    this._engineGlow = mesh;
    return mesh;
  }

  getChargeState() {
    return {
      isCharging: !!this._isCharging,
      progress: Math.max(0, Math.min(1, Number(this._chargeProgress) || 0)),
      lockStrength: Math.max(0, Math.min(1, Number(this._lockStrength) || 0)),
      hasLock: !!this._lockTarget && (this._lockStrength || 0) > 0.7,
    };
  }

  _resetCharge() {
    this._isCharging = false;
    this._chargeStartAtMs = 0;
    this._chargeProgress = 0;
    this._lockTarget = null;
    this._lockStrength = 0;
    this._lockHeldMs = 0;
  }

  fireChargedShot({ target = null } = {}) {
    const enemyMgr = this.game?.enemyManager;
    if (!enemyMgr) return;
    const create = enemyMgr.createProjectileWithOptions || enemyMgr.createProjectile;

    const shipPos = this.object3D.position.clone();
    const q = this.object3D.quaternion;
    const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(q).normalize();
    const origin = shipPos.clone().add(forward.clone().multiplyScalar(34));

    const baseDir = target?.object3D?.position?.isVector3
      ? new THREE.Vector3().subVectors(target.object3D.position, origin).normalize()
      : (this.game?.getAimDirection?.(origin) || new THREE.Vector3(0, 0, -1));

    const projectile = create.call(
      enemyMgr,
      origin,
      baseDir,
      280,
      Math.max(20, Math.round(this.getProjectileDamage() * 2.5)),
      true,
      {
        isCharged: true,
        color: 0x66ff66,
        homingTarget: target || null,
        homingTurnRate: 7.0,
        explosionRadius: 85,
        splashDamageFactor: 0.65,
      }
    );

    this.game.scene?.add?.(projectile.object3D);
    if (Array.isArray(this.game.projectiles)) this.game.projectiles.push(projectile);

    // SFX / feedback (best effort).
    this.game?.soundManager?.playSFX?.('laser');
    this.game?.feedback?.shakeScreen?.(0.08, 0.12);
  }

  updateChargeLock(deltaTime = 0.016) {
    if (!this._isCharging) return;

    const cam = this.game?.camera;
    const ui = this.game?.uiManager;
    const renderer = this.game?.renderer;
    if (!cam || !ui || !renderer) return;

    const ret = ui.getReticleScreenPos?.(renderer);
    if (!ret) return;

    const enemies = this.game?.enemies || [];
    const lockRadiusPx = 52;

    let best = null;
    let bestDist = Infinity;
    for (const e of enemies) {
      if (!e?.object3D || e.isDestroyed) continue;
      const p = e.object3D.position.clone();
      const v = p.project(cam);
      if (!Number.isFinite(v.x) || !Number.isFinite(v.y) || !Number.isFinite(v.z)) continue;
      if (v.z > 1) continue;

      const sx = (v.x * 0.5 + 0.5) * ret.width;
      const sy = (-(v.y * 0.5) + 0.5) * ret.height;
      const dx = sx - ret.x;
      const dy = sy - ret.y;
      const d = Math.hypot(dx, dy);
      if (d <= lockRadiusPx && d < bestDist) {
        best = e;
        bestDist = d;
      }
    }

    const dt = Math.max(0, Number(deltaTime) || 0.016);
    const lockRise = 1 - Math.exp(-6.5 * dt);
    const lockFall = 1 - Math.exp(-9.5 * dt);

    if (best) {
      if (this._lockTarget !== best) {
        this._lockTarget = best;
        this._lockStrength = 0;
        this._lockHeldMs = 0;
      }
      this._lockHeldMs += dt * 1000;
      const gain = Math.max(0.15, 1 - (bestDist / lockRadiusPx));
      const targetStrength = Math.max(0, Math.min(1, (this._chargeProgress || 0) * 0.65 + gain * 0.6));
      this._lockStrength = THREE.MathUtils.lerp(this._lockStrength, targetStrength, lockRise);
    } else {
      this._lockStrength = THREE.MathUtils.lerp(this._lockStrength, 0, lockFall);
      if (this._lockStrength < 0.05) this._lockTarget = null;
    }
  }

  getNowMs() {
    return (globalThis.performance && typeof globalThis.performance.now === "function")
      ? globalThis.performance.now()
      : Date.now();
  }

  isSpecialActive(nowMs = this.getNowMs()) {
    return nowMs < (this._specialActiveUntilMs || 0);
  }

  isSpecialReady(nowMs = this.getNowMs()) {
    return nowMs >= (this._specialReadyAtMs || 0);
  }

  getSpecialCooldownRemainingMs(nowMs = this.getNowMs()) {
    return Math.max(0, (this._specialReadyAtMs || 0) - nowMs);
  }

  getSpecialActiveRemainingMs(nowMs = this.getNowMs()) {
    return Math.max(0, (this._specialActiveUntilMs || 0) - nowMs);
  }

  activateSpecial(_nowMs) {
    // Overridden by subclasses that have a special ability.
    return false;
  }

  async loadActivatedShip(art) {
    if (!art?.shipGlbUrlActivated || isTestEnvironment()) return null;
    try {
      let gltf = null;
      const attempts = 3;
      for (let attempt = 0; attempt < attempts; attempt += 1) {
        try {
          gltf = await this.loadGLB(art.shipGlbUrlActivated, { logFailures: attempt === attempts - 1 });
          break;
        } catch (err) {
          if (attempt === attempts - 1) throw err;
          await new Promise((resolve) => setTimeout(resolve, 350 * (attempt + 1)));
        }
      }
      const root = gltf?.scene || gltf?.scenes?.[0];
      if (!root) return null;

      const shipGroup = new THREE.Group();
      shipGroup.name = "ship-glb-activated";
      shipGroup.add(root);

      const box = new THREE.Box3().setFromObject(root);
      const center = box.getCenter(new THREE.Vector3());
      root.position.sub(center);

      const size = box.getSize(new THREE.Vector3());
      const maxDim = Math.max(size.x || 1, size.y || 1, size.z || 1);
      const scale = 70 / maxDim;
      shipGroup.scale.setScalar(scale);

      const rot = art.shipGlbRotation;
      if (rot && typeof rot === "object") {
        if (Number.isFinite(rot.x)) shipGroup.rotation.x += rot.x;
        if (Number.isFinite(rot.y)) shipGroup.rotation.y += rot.y;
        if (Number.isFinite(rot.z)) shipGroup.rotation.z += rot.z;
      }

      shipGroup.position.set(0, 4, 8);
      shipGroup.visible = false;

      shipGroup.traverse((obj) => {
        if (!obj || !obj.isMesh) return;
        obj.castShadow = false;
        obj.receiveShadow = false;
        const material = obj.material;
        if (material && typeof material === "object") {
          if (material.map) material.map.needsUpdate = true;
          material.needsUpdate = true;
        }
      });

      return shipGroup;
    } catch {
      return null;
    }
  }

  ensureActivatedShipLoading() {
    if (this.shipActivated) return Promise.resolve(this.shipActivated);
    if (this.shipActivatedPromise) return this.shipActivatedPromise;
    const art = getCharacterArt(this.character);
    const hasCustomLoader = this.loadActivatedShip !== Player.prototype.loadActivatedShip;
    if (!art?.shipGlbUrlActivated || (isTestEnvironment() && !hasCustomLoader)) return Promise.resolve(null);
    this.shipActivatedPromise = Promise.resolve()
      .then(() => this.loadActivatedShip(art))
      .then((group) => {
        if (!group) this.shipActivatedPromise = null;
        return group || null;
      })
      .catch(() => {
        this.shipActivatedPromise = null;
        return null;
      });
    return this.shipActivatedPromise;
  }

  showActivatedShip(visible) {
    this._activatedShipWanted = !!visible;
    if (visible) {
      this.ensureActivatedShipLoading();
      if (this.shipActivatedPromise && !this.shipActivated) {
        this.shipActivatedPromise.then((group) => {
          if (group && !this.shipActivated) {
            this.shipActivated = group;
            this.object3D.add(group);
            this.shipActivated.visible = !!this._activatedShipWanted;
            if (this.ship) this.ship.visible = !this._activatedShipWanted;
          }
        });
      } else if (this.shipActivated) {
        this.shipActivated.visible = true;
        if (this.ship) this.ship.visible = false;
      }
    } else {
      if (this.shipActivated) {
        this.shipActivated.visible = false;
      }
      if (this.ship) {
        this.ship.visible = true;
      }
    }
  }

  tryActivateSpecial(nowMs = this.getNowMs()) {
    if (!this.specialName) return false;
    // Don’t allow re-triggering while the ability is active (prevents spam/stacking).
    if (this.isSpecialActive(nowMs)) return false;
    if (!this.isSpecialReady(nowMs)) return false;
    const ok = this.activateSpecial(nowMs);
    if (!ok) return false;

    this.showActivatedShip(true);
    this.maybeShowSpecialBriefing();

    const durationMs = Math.max(0, Number(this.specialDurationMs) || 0);
    if (durationMs > 0) {
      this._specialActiveUntilMs = Math.max(this._specialActiveUntilMs || 0, nowMs + durationMs);
    }

    const cd = Math.max(0, Number(this.specialCooldownMs) || 0);
    this._specialReadyAtMs = nowMs + cd;

    // Bold, readable activation cue (best effort).
    try {
      this.game?.feedback?.playSpecialEffect?.({ character: this.character, name: this.specialName });
    } catch {
      // ignore
    }
    return true;
  }

  getSpecialBriefing() {
    if (!this.specialName) return null;
    const hint = this.specialButtonHint || "E";
    return {
      character: this.character,
      name: this.specialName,
      hint,
      summary: this.specialSummary || `${this.specialName} is this ship's special ability.`,
      usage: this.specialUsage || `Press ${hint} when the SPECIAL HUD says READY.`,
      effect: this.specialEffect || "This ability changes combat state immediately when activated.",
      tacticalTip: this.specialTacticalTip || "Use it deliberately; the cooldown is long.",
    };
  }

  getSpecialBriefingStorageKey() {
    const character = String(this.character || "pilot").toLowerCase().replace(/[^a-z0-9_-]/g, "");
    const ability = String(this.specialName || "special").toLowerCase().replace(/[^a-z0-9_-]/g, "");
    return `sm64_special_brief_seen_${character}_${ability}`;
  }

  maybeShowSpecialBriefing() {
    if (this._specialBriefingShown || !this.specialName) return false;
    const show = this.game?.uiManager?.showSpecialAbilityBriefing;
    if (typeof show !== "function") return false;

    const briefing = this.getSpecialBriefing();
    if (!briefing) return false;
    this._specialBriefingShown = true;
    show.call(this.game.uiManager, briefing);
    return true;
  }

  getSpecialHudState(nowMs = this.getNowMs()) {
    if (!this.specialName) return null;
    const cooldownMs = Math.max(0, Number(this.specialCooldownMs) || 0);
    const cooldownRemainingMs = this.getSpecialCooldownRemainingMs(nowMs);
    const activeRemainingMs = this.getSpecialActiveRemainingMs(nowMs);
    const isActive = activeRemainingMs > 0;
    const isReady = this.isSpecialReady(nowMs) && !isActive;
    const progress =
      cooldownMs > 0 ? Math.max(0, Math.min(1, 1 - cooldownRemainingMs / cooldownMs)) : (isReady ? 1 : 0);

    return {
      name: this.specialName,
      hint: this.specialButtonHint || "E",
      isReady,
      isActive,
      cooldownMs,
      cooldownRemainingMs,
      activeRemainingMs,
      specialDurationMs: Math.max(0, Number(this.specialDurationMs) || 0),
      progress,
    };
  }

  getShieldVisual() {
    const visuals = {
      honeybadger: { color: 0xff6600, emissive: 0xff3300, opacity: 0.22 },
      octopus: { color: 0x00ffff, emissive: 0x0088ff, opacity: 0.18 },
      elephant: { color: 0x00ffff, emissive: 0x004488, opacity: 0.2 },
      mantisshrimp: { color: 0xff00ff, emissive: 0xff00ff, opacity: 0.18 },
      ant: { color: 0x00ff66, emissive: 0x00aa00, opacity: 0.18 },
    };
    return visuals[this.character] || { color: 0x00ffff, emissive: 0x008888, opacity: 0.18 };
  }

  ensureShieldMesh() {
    if (this.shieldMesh) return;
    const { color, emissive, opacity } = this.getShieldVisual();
    const shieldGeometry = new THREE.SphereGeometry(15.5, 16, 16);
    const shieldMaterial = new THREE.MeshPhongMaterial({
      color,
      transparent: true,
      opacity: Math.max(0.05, Number(opacity) || 0.18),
      side: THREE.DoubleSide,
      emissive,
      emissiveIntensity: 0.25,
      depthWrite: false,
    });
    this.shieldMesh = new THREE.Mesh(shieldGeometry, shieldMaterial);
    this.shieldMesh.name = "player-shield";
    this.shieldMesh.visible = this.shields > 0;
    this.object3D.add(this.shieldMesh);
  }

  updateShieldVisual(deltaTime) {
    if (!this.shieldMesh) return;
    const frac =
      this.maxShields > 0 ? Math.max(0, Math.min(1, this.shields / this.maxShields)) : 0;
    this.shieldMesh.visible = this.shields > 0;
    if (!this.shieldMesh.visible) return;

    this._shieldPulse = (this._shieldPulse || 0) + Math.max(0, deltaTime);
    const pulse = 0.5 + 0.5 * Math.sin(this._shieldPulse * 3.2);
    const baseOpacity = this.getShieldVisual().opacity || 0.18;
    const targetOpacity = Math.max(0.06, baseOpacity * (0.35 + 0.65 * frac) + pulse * 0.03);

    const material = this.shieldMesh.material;
    if (material && typeof material === "object" && "opacity" in material) {
      material.opacity = targetOpacity;
      material.needsUpdate = true;
    }

    const rot = this.shieldMesh.rotation;
    if (rot && typeof rot === "object") {
      if (typeof rot.y === "number") rot.y += deltaTime * 0.55;
      if (typeof rot.x === "number") rot.x += deltaTime * 0.25;
    }

    const s = 1 + pulse * 0.02;
    if (this.shieldMesh.scale && typeof this.shieldMesh.scale.set === "function") {
      this.shieldMesh.scale.set(s, s, s);
    }
  }

  getProjectileDamage() {
    const d = Number(this.damage);
    return Number.isFinite(d) ? d : 10;
  }

  getProjectileSpeed() {
    return 200;
  }

  getIncomingDamage(amount) {
    return amount;
  }

  ensureCollider() {
    if (this.collider) return;
    const colliderGeometry = new THREE.BoxGeometry(40, 18, 55);
    const colliderMaterial = new THREE.MeshBasicMaterial({ transparent: true, opacity: 0 });
    this.collider = new THREE.Mesh(colliderGeometry, colliderMaterial);
    this.collider.name = "player-collider";
    this.collider.visible = false;
    this.object3D.add(this.collider);
  }

  loadTexture(url) {
    return new Promise((resolve, reject) => {
      const loader = new THREE.TextureLoader();
      loader.load(
        url,
        (texture) => resolve(texture),
        undefined,
        (err) => reject(err)
      );
    });
  }

  async loadGLB(url, { logFailures = true } = {}) {
    const loader = await getConfiguredGLTFLoader();
    return new Promise((resolve, reject) => {
      loader.load(url, resolve, undefined, (err) => {
        if (logFailures) {
          try {
            const key = String(url || "");
            const once = Player._glbErrorLogged || (Player._glbErrorLogged = new Set());
            if (!once.has(key)) {
              once.add(key);
              console.warn("[glb] Failed to load:", key, err);
            }
          } catch {
            // ignore
          }
        }
        reject(err);
      });
    });
  }

  static _alphaTextureCache = new Map();
  static _glbTemplateCache = new Map();

  static _makeGlbTemplateKey(url, { targetMaxDim, rotation } = {}) {
    const rot = rotation && typeof rotation === "object"
      ? `r:${Number(rotation.x) || 0},${Number(rotation.y) || 0},${Number(rotation.z) || 0}`
      : "r:0,0,0";
    return `${String(url)}|s:${Number(targetMaxDim) || 0}|${rot}`;
  }

  static _cloneMaterial(material) {
    if (!material) return material;
    if (Array.isArray(material)) return material.map((m) => Player._cloneMaterial(m));
    if (typeof material.clone === "function") return material.clone();
    return material;
  }

  /**
   * Load + normalize a GLB once, then clone it for cheap instancing.
   * Used for small “ability” models (drones, shockwaves, charge cores, etc).
   */
  async createGlbInstance(url, { targetMaxDim = 10, rotation = null, cloneMaterials = false } = {}) {
    if (!url) return null;
    // Tests run in jsdom without an asset server; avoid hanging on GLB fetches.
    if (isTestEnvironment()) return null;

    const key = Player._makeGlbTemplateKey(url, { targetMaxDim, rotation });
    if (!Player._glbTemplateCache.has(key)) {
      Player._glbTemplateCache.set(
        key,
        (async () => {
          try {
            const gltf = await this.loadGLB(url);
            const root = gltf?.scene || gltf?.scenes?.[0];
            if (!root) return null;

            const group = new THREE.Group();
            group.name = `glb-template:${key}`;
            group.add(root);

            // Center around bounding box.
            const box = new THREE.Box3().setFromObject(root);
            const center = box.getCenter(new THREE.Vector3());
            root.position.sub(center);

            // Scale so max dimension is targetMaxDim.
            const size = box.getSize(new THREE.Vector3());
            const maxDim = Math.max(size.x || 1, size.y || 1, size.z || 1);
            const s = (Number(targetMaxDim) || maxDim) / maxDim;
            if (Number.isFinite(s) && s > 0) group.scale.setScalar(s);

            if (rotation && typeof rotation === "object") {
              if (Number.isFinite(rotation.x)) group.rotation.x += rotation.x;
              if (Number.isFinite(rotation.y)) group.rotation.y += rotation.y;
              if (Number.isFinite(rotation.z)) group.rotation.z += rotation.z;
            }

            group.traverse((obj) => {
              if (!obj?.isMesh) return;
              obj.castShadow = false;
              obj.receiveShadow = false;
              const mat = obj.material;
              if (mat && typeof mat === "object") {
                mat.side = THREE.DoubleSide;
                mat.needsUpdate = true;
              }
            });

            return group;
          } catch {
            return null;
          }
        })()
      );
    }

    let template = null;
    try {
      template = await Player._glbTemplateCache.get(key);
    } catch {
      template = null;
    }
    if (!template) {
      // Do not permanently cache failed/null template loads; allow future retries.
      Player._glbTemplateCache.delete(key);
      return null;
    }

    const inst = template.clone(true);
    if (cloneMaterials) {
      inst.traverse((obj) => {
        if (!obj?.isMesh) return;
        obj.material = Player._cloneMaterial(obj.material);
      });
    }
    return inst;
  }

  /**
   * The jam art is often exported on a solid background (no alpha channel).
   * To avoid the "giant rectangle sprite" look, we auto-key out the dominant
   * background color (sampled from the corners).
   */
  toAutoAlphaTexture(texture, { threshold = 48, softness = 32 } = {}) {
    const cacheKey = texture?.source?.data?.currentSrc || texture?.image?.currentSrc;
    if (cacheKey && Player._alphaTextureCache.has(cacheKey)) {
      return Player._alphaTextureCache.get(cacheKey);
    }

    const image = texture?.image;
    if (!image || typeof document === "undefined") return texture;

    const width = image.naturalWidth || image.width;
    const height = image.naturalHeight || image.height;
    if (!width || !height) return texture;

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return texture;

    ctx.drawImage(image, 0, 0, width, height);
    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;

    const sampleBlock = (sx, sy, size = 6) => {
      let r = 0, g = 0, b = 0, n = 0;
      const x0 = Math.max(0, Math.min(width - size, sx));
      const y0 = Math.max(0, Math.min(height - size, sy));
      for (let y = y0; y < y0 + size; y++) {
        for (let x = x0; x < x0 + size; x++) {
          const i = (y * width + x) * 4;
          r += data[i];
          g += data[i + 1];
          b += data[i + 2];
          n++;
        }
      }
      return { r: r / n, g: g / n, b: b / n };
    };

    const c1 = sampleBlock(0, 0);
    const c2 = sampleBlock(width - 7, 0);
    const c3 = sampleBlock(0, height - 7);
    const c4 = sampleBlock(width - 7, height - 7);
    const bg = {
      r: (c1.r + c2.r + c3.r + c4.r) / 4,
      g: (c1.g + c2.g + c3.g + c4.g) / 4,
      b: (c1.b + c2.b + c3.b + c4.b) / 4,
    };

    const t0 = threshold;
    const t1 = threshold + softness;
    for (let i = 0; i < data.length; i += 4) {
      const dr = data[i] - bg.r;
      const dg = data[i + 1] - bg.g;
      const db = data[i + 2] - bg.b;
      const dist = Math.sqrt(dr * dr + dg * dg + db * db);

      if (dist <= t0) {
        data[i + 3] = 0;
      } else if (dist < t1) {
        data[i + 3] = Math.round(((dist - t0) / softness) * 255);
      } else {
        data[i + 3] = 255;
      }
    }

    ctx.putImageData(imageData, 0, 0);

    const out = new THREE.CanvasTexture(canvas);
    out.flipY = texture.flipY;
    out.colorSpace = THREE.SRGBColorSpace;
    out.needsUpdate = true;

    if (cacheKey) Player._alphaTextureCache.set(cacheKey, out);
    return out;
  }

  async tryCreateArtShip() {
    // Explicit `?art=0` disables all custom art (useful for debugging/perf).
    if (getArtModeOverride() === 0) return false;

    const art = getCharacterArt(this.character);
    if (!art?.shipGlbUrl && !art?.shipUrl) return false;

    // Tests run in jsdom without an asset server; avoid hanging on GLB fetches.
    if (isTestEnvironment()) return false;

    try {
      // Prefer true 3D if available.
      if (art.shipGlbUrl) {
        const gltf = await this.loadGLB(art.shipGlbUrl);
        const root = gltf?.scene || gltf?.scenes?.[0];
        if (!root) return false;

        const shipGroup = new THREE.Group();
        shipGroup.name = "ship-glb";
        shipGroup.add(root);

        // Normalize: center the model around its bounding box.
        const box = new THREE.Box3().setFromObject(root);
        const center = box.getCenter(new THREE.Vector3());
        root.position.sub(center);

        // Scale to roughly match the existing ship footprint (and the fixed collider).
        const size = box.getSize(new THREE.Vector3());
        const maxDim = Math.max(size.x || 1, size.y || 1, size.z || 1);
        const scale = 70 / maxDim;
        shipGroup.scale.setScalar(scale);

        // Optional per-ship rotation overrides (fixes facing direction per export).
        const rot = art.shipGlbRotation;
        if (rot && typeof rot === "object") {
          if (Number.isFinite(rot.x)) shipGroup.rotation.x += rot.x;
          if (Number.isFinite(rot.y)) shipGroup.rotation.y += rot.y;
          if (Number.isFinite(rot.z)) shipGroup.rotation.z += rot.z;
        }

        shipGroup.position.set(0, 4, 8);

        shipGroup.traverse((obj) => {
          if (!obj || !obj.isMesh) return;
          obj.castShadow = false;
          obj.receiveShadow = false;

          const material = obj.material;
          if (material && typeof material === "object") {
            // Preserve authored materials/textures; do not replace or flatten.
            if (material.map) {
              material.map = material.map; // ensure map stays enabled
              material.map.needsUpdate = true;
            }
            material.needsUpdate = true;
          }
        });

        this.ship = shipGroup;
        this.object3D.add(shipGroup);

        // Start loading activated ship variant in background.
        if (art.shipGlbUrlActivated) {
          this.ensureActivatedShipLoading();
        }

        return true;
      }

      // 2D ship sprites only load when art mode is enabled (they’re a stylistic/experimental fallback).
      if (!isArtEnabled()) return false;

      const loaded = await this.loadTexture(art.shipUrl);
      const texture = this.toAutoAlphaTexture(loaded);
      // Favor a crisp, N64-ish read over browser smoothing.
      texture.magFilter = THREE.NearestFilter;
      texture.minFilter = THREE.NearestMipmapNearestFilter;
      texture.anisotropy = 1;
      texture.needsUpdate = true;

      const shipMaterial = new THREE.MeshBasicMaterial({
        map: texture,
        transparent: true,
        alphaTest: 0.12,
        depthWrite: true,
        side: THREE.DoubleSide,
      });
      const shipPlane = new THREE.Mesh(new THREE.PlaneGeometry(90, 60), shipMaterial);
      shipPlane.name = "ship-plane";
      shipPlane.position.set(0, 4, 8);

      // Silhouette outline using the same alpha mask (no big opaque rectangle).
      const outlineMaterial = new THREE.MeshBasicMaterial({
        map: texture,
        color: 0x000000,
        transparent: true,
        opacity: 0.95,
        alphaTest: 0.12,
        depthWrite: false,
        side: THREE.DoubleSide,
      });
      const outlinePlane = new THREE.Mesh(new THREE.PlaneGeometry(90, 60), outlineMaterial);
      outlinePlane.name = "ship-outline";
      outlinePlane.scale.set(1.06, 1.06, 1);
      outlinePlane.position.set(0, 4, 7.4);

      this.ship = shipPlane;
      this.object3D.add(outlinePlane);
      this.object3D.add(shipPlane);
      return true;
    } catch {
      return false;
    }
  }
  
  async createShip() {
    // Always create a fast placeholder first so gameplay is immediate.
    // If the GLB loads successfully, we swap it in below.
    this.clearShipVisual();

    // Create placeholder ship (will be replaced with actual models)
    const shipGeometry = new THREE.ConeGeometry(7, 26, 8);
    shipGeometry.rotateX(Math.PI / 2);
    
    const shipMaterial = new THREE.MeshPhongMaterial({
      color: this.getCharacterColor(),
      emissive: this.getCharacterColor(),
      emissiveIntensity: 0.55,
      shininess: 100,
    });
    
    this.ship = new THREE.Mesh(shipGeometry, shipMaterial);
    this.ship.castShadow = true;
    this.object3D.add(this.ship);
    this._shipVisualNodes.push(this.ship);

    // Outline pass for readability
    const outlineMaterial = new THREE.MeshBasicMaterial({
      color: 0x000000,
      side: THREE.BackSide,
    });
    const outline = new THREE.Mesh(shipGeometry, outlineMaterial);
    outline.scale.set(1.12, 1.12, 1.12);
    this.object3D.add(outline);
    this._shipVisualNodes.push(outline);
    
    // Add cockpit
    const cockpitGeometry = new THREE.SphereGeometry(2.6, 10, 10);
    const cockpitMaterial = new THREE.MeshPhongMaterial({
      color: 0x00ffff,
      emissive: 0x008888,
      emissiveIntensity: 0.5,
      transparent: true,
      opacity: 0.7,
    });
    const cockpit = new THREE.Mesh(cockpitGeometry, cockpitMaterial);
    cockpit.position.set(0, 2.5, 7);
    this.object3D.add(cockpit);
    this._shipVisualNodes.push(cockpit);
    
    // Add wings
    const wingGeometry = new THREE.BoxGeometry(40, 1.2, 10);
    const wingMaterial = new THREE.MeshPhongMaterial({
      color: this.getCharacterColor(),
    });
    const wings = new THREE.Mesh(wingGeometry, wingMaterial);
    wings.position.set(0, 0, 7);
    this.object3D.add(wings);
    this._shipVisualNodes.push(wings);

    // Swap in the GLB ship asynchronously (best effort).
    const token = ++this._shipLoadToken;
    this.tryCreateArtShip()
      .then((ok) => {
        if (!ok) return;
        if (this._disposed) return;
        if (token !== this._shipLoadToken) return;
        // `tryCreateArtShip()` already attached the GLB to `object3D` and set `this.ship`.
        // Remove the placeholder visuals (if they’re still present).
        this.clearShipVisual({ keepCurrentShip: true });
      })
      .catch(() => {});
  }

  clearShipVisual({ keepCurrentShip = false } = {}) {
    const nodes = Array.isArray(this._shipVisualNodes) ? this._shipVisualNodes : [];
    for (const n of nodes) {
      if (!n) continue;
      try {
        this.object3D.remove(n);
      } catch {
        // ignore
      }
      // Free placeholder GPU resources (GLB ship disposal is handled by `dispose()`).
      try {
        n.traverse?.((child) => {
          if (child?.geometry && typeof child.geometry.dispose === "function") child.geometry.dispose();
          if (child?.material) {
            const disposeMaterial = (material) => {
              if (!material) return;
              if (Array.isArray(material)) return material.forEach(disposeMaterial);
              for (const key of ["map", "emissiveMap", "normalMap", "roughnessMap", "metalnessMap", "aoMap", "alphaMap"]) {
                const tex = material[key];
                if (tex && typeof tex.dispose === "function") tex.dispose();
              }
              if (typeof material.dispose === "function") material.dispose();
            };
            disposeMaterial(child.material);
          }
        });
      } catch {
        // ignore
      }
    }
    this._shipVisualNodes = [];

    if (!keepCurrentShip && this.ship) {
      try {
        this.object3D.remove(this.ship);
      } catch {
        // ignore
      }
      this.ship = null;
    }
  }
  
  getCharacterColor() {
    const colors = {
      honeybadger: 0xff6600,
      octopus: 0xffffff,
      elephant: 0x888888,
      mantisshrimp: 0xff00ff,
      ant: 0x00aa00,
    };
    if (this.color) {
      try {
        return new THREE.Color(this.color).getHex();
      } catch {
        // fall through
      }
    }
    return colors[this.character] || 0x00c6ff;
  }
  
  createTrail() {
    const trailGeometry = new THREE.BufferGeometry();
    const trailCount = 50;
    const positions = new Float32Array(trailCount * 3);
    
    for (let i = 0; i < trailCount * 3; i += 3) {
      positions[i] = 0;
      positions[i + 1] = 0;
      positions[i + 2] = i * 2;
    }
    
    trailGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    
    const trailMaterial = new THREE.LineBasicMaterial({
      color: this.getCharacterColor(),
      transparent: true,
      opacity: 0.5,
    });
    
    this.trail = new THREE.Line(trailGeometry, trailMaterial);
    this.object3D.add(this.trail);
  }
  
  update(deltaTime, input) {
    const nowMs = this.getNowMs();
    if (input?.special) {
      this.tryActivateSpecial(nowMs);
      input.special = false;
    }
    if (input?.roll) {
      this.barrelRoll();
      // Edge-trigger: consume the input so holding the key/button doesn't spam rolls.
      input.roll = false;
    }

    // Fire input: tap for normal shots, hold to charge, release for charged/lock-on shot.
    const fireDown = !!input?.fire;
    const chargeGateMs = 160; // hold beyond this starts charging
    const chargeFullMs = 520; // time to full charge
    if (fireDown && !this._fireWasDown) {
      this._fireDownAtMs = nowMs;
      this._isCharging = false;
      this._chargeStartAtMs = 0;
      this._chargeProgress = 0;
      this._lockTarget = null;
      this._lockStrength = 0;
      this._lockHeldMs = 0;
    }

    if (fireDown) {
      const heldMs = Math.max(0, nowMs - (this._fireDownAtMs || nowMs));
      if (!this._isCharging && heldMs >= chargeGateMs) {
        this._isCharging = true;
        this._chargeStartAtMs = nowMs - (heldMs - chargeGateMs);
        this._chargeProgress = 0;
        this._lockTarget = null;
        this._lockStrength = 0;
        this._lockHeldMs = 0;
      }
      if (this._isCharging) {
        const t = Math.max(0, nowMs - (this._chargeStartAtMs || nowMs));
        this._chargeProgress = Math.max(0, Math.min(1, t / Math.max(1, chargeFullMs)));
      }
    }

    if (!fireDown && this._fireWasDown) {
      if (this._isCharging) {
        const target = (this._lockTarget && (this._lockStrength || 0) > 0.7) ? this._lockTarget : null;
        this.fireChargedShot({ target });
      } else {
        this.fire();
      }
      this._resetCharge();
    }
    this._fireWasDown = fireDown;

    // Handle barrel roll
    if (this.isBarrelRolling) {
      this.barrelRollTimer -= deltaTime;
      if (this.barrelRollTimer <= 0) {
        this.isBarrelRolling = false;
      }
      this.object3D.rotation.z += (Math.PI * 2) * deltaTime / this.barrelRollTime;
    }

    // Handle movement
    if (!this.isBarrelRolling) {
      // Forward/backward
      let currentSpeed = this.speed;
      let wasBoosting = this._wasBoosting || false;
      this.isBoosting = Boolean(input?.boost && this.boost > 0);
      this.isBraking = Boolean(input?.brake);
      const isBoosting = this.isBoosting;

      // Engine glow reacts to boost/brake (pure additive; does not alter GLB materials).
      try {
        const g = this._engineGlow;
        const m = g?.material;
        if (g && m) {
          const base = 0.28;
          const op = base + (isBoosting ? 0.22 : 0) - (this.isBraking ? 0.1 : 0);
          m.opacity = Math.max(0.08, Math.min(0.65, op));
          const s = 1 + (isBoosting ? 0.35 : 0) - (this.isBraking ? 0.12 : 0);
          g.scale.setScalar(Math.max(0.6, s));
          m.needsUpdate = true;
        }
      } catch {
        // ignore
      }

      if (this.isBoosting) {
        currentSpeed = this.boostSpeed;
        this.boost = Math.max(0, this.boost - 30 * deltaTime);
        // Play boost sound on activation
        if (!wasBoosting && this.game.soundManager?.playCharacterSound) {
          this.game.soundManager.playCharacterSound(this.character, 'boost');
        }
        if (!wasBoosting && this.onBoost) this.onBoost();
        this._wasBoosting = true;
      } else if (this.isBraking) {
        currentSpeed = this.brakeSpeed;
        this.boost = Math.min(this.maxBoost, this.boost + 10 * deltaTime);
        this._wasBoosting = false;
      } else {
        this.boost = Math.min(this.maxBoost, this.boost + 5 * deltaTime);
        this._wasBoosting = false;
      }

      this.game.uiManager?.updateBoost?.(this.boost, this.maxBoost);
      
      this.currentSpeed = currentSpeed;
      this.forwardVelocity = currentSpeed;

      // Star Fox 64-style movement:
      // 1) Raw input moves a bounded target lane immediately.
      // 2) The ship springs tightly toward that target; velocity is derived output, not drift.
      // 3) Rotation is visual feedback only and returns neutral when input stops.
      const analogX = Number(input?.stickX);
      const analogY = Number(input?.stickY);
      const digitalX = (input?.right ? 1 : 0) + (input?.left ? -1 : 0);
      const digitalY = (input?.forward ? 1 : 0) + (input?.backward ? -1 : 0);
      const inputDevice = String(input?.device || "").toLowerCase();
      const keyboardLike = inputDevice !== "gamepad";

      let rawStickX = Number.isFinite(analogX) ? analogX : digitalX;
      let rawStickY = Number.isFinite(analogY) ? analogY : digitalY;
      rawStickX = THREE.MathUtils.clamp(rawStickX, -1, 1);
      rawStickY = THREE.MathUtils.clamp(rawStickY, -1, 1);

      const deadzone = inputDevice === "gamepad" ? 0.04 : 0;
      if (Math.abs(rawStickX) < deadzone) rawStickX = 0;
      if (Math.abs(rawStickY) < deadzone) rawStickY = 0;

      const dt = Math.max(0, Number(deltaTime) || 0);
      const stickFilterRate = keyboardLike ? 18 : 14;
      const stickFilterT = 1 - Math.exp(-stickFilterRate * dt);
      this._stickFiltered.x = THREE.MathUtils.lerp(this._stickFiltered.x, rawStickX, stickFilterT);
      this._stickFiltered.y = THREE.MathUtils.lerp(this._stickFiltered.y, rawStickY, stickFilterT);
      const stickX = this._stickFiltered.x;
      const stickY = this._stickFiltered.y;

      // Slower smoothing for reticle aiming so it drifts instead of jumping.
      const reticleSmoothRate = keyboardLike ? 10 : 8;
      const reticleSmoothT = 1 - Math.exp(-reticleSmoothRate * dt);
      this._reticleStick.x = THREE.MathUtils.lerp(this._reticleStick.x, stickX, reticleSmoothT);
      this._reticleStick.y = THREE.MathUtils.lerp(this._reticleStick.y, stickY, reticleSmoothT);

      // Input is transformed by bank, matching Arwing rails behavior.
      const bankTransform = -(this._bankAngle || 0) * (keyboardLike ? 0.45 : 1);
      const cb = Math.cos(bankTransform);
      const sb = Math.sin(bankTransform);
      const steerX = stickX * cb - stickY * sb;
      const steerY = stickX * sb + stickY * cb;
      const rawSteerX = rawStickX * cb - rawStickY * sb;
      const rawSteerY = rawStickX * sb + rawStickY * cb;

      const bx = Number(this.laneBounds?.x) || 180;
      const by = Number(this.laneBounds?.y) || 115;
      const nearFloor = this.position.y <= (-by + 20);
      if (!this._targetLane) this._targetLane = new THREE.Vector2(this.position.x, this.position.y);
      if (!Number.isFinite(this._targetLane.x) || !Number.isFinite(this._targetLane.y)) {
        this._targetLane.set(this.position.x, this.position.y);
      }

      // Steering into the bank increases turn response (classic Star Fox feel).
      let turnBoost = 1.0;
      if ((this._bankAngle > 0.2 && steerX > 0) || (this._bankAngle < -0.2 && steerX < 0)) {
        turnBoost = 1.9;
      }
      if (this.isBarrelRolling) {
        turnBoost = 2.6;
      }

      const maxYaw = 0.28;
      const maxPitch = 0.22;
      const maxBank = 0.58;
      const maxRoll = 0.48;

      const targetYaw = steerX * maxYaw;
      let targetPitch = -steerY * maxPitch;
      if (nearFloor && targetPitch > 0) targetPitch *= 0.25;
      if (this.isBraking) targetPitch += 0.1;

      const rollFactor = Math.abs(steerX) < 0.02 ? 1.0 : 3.2;
      const targetBank = -steerX * maxBank;
      const bankT = 1 - Math.exp(-(isBoosting ? 8.5 : 6.8) * rollFactor * dt);
      this._bankAngle = THREE.MathUtils.lerp(this._bankAngle, targetBank, bankT);

      const yawRate = keyboardLike ? 7.6 : 6.4;
      const pitchRate = keyboardLike ? 6.8 : 5.8;
      const turnT = 1 - Math.exp(-(yawRate * turnBoost) * dt);
      const pitchT = 1 - Math.exp(-(pitchRate + (nearFloor ? 2.2 : 0)) * dt);
      this.rotation.y = THREE.MathUtils.lerp(this.rotation.y, targetYaw, turnT);
      this.rotation.x = THREE.MathUtils.lerp(this.rotation.x, targetPitch, pitchT);
      this.rotation.z = THREE.MathUtils.clamp(this._bankAngle * 0.9, -maxRoll, maxRoll);

      const targetRateScale = isBoosting ? 1.08 : (this.isBraking ? 0.68 : 1);
      const targetRateX = (keyboardLike ? 210 : 260) * targetRateScale;
      const targetRateY = (keyboardLike ? 150 : 190) * targetRateScale;
      this._targetLane.x = THREE.MathUtils.clamp(
        this._targetLane.x + rawSteerX * targetRateX * dt,
        -bx,
        bx
      );
      this._targetLane.y = THREE.MathUtils.clamp(
        this._targetLane.y + rawSteerY * targetRateY * dt,
        -by,
        by
      );

      this.targetPosition.set(this._targetLane.x, this._targetLane.y, this.position.z);

      const prevX = this.position.x;
      const prevY = this.position.y;
      const followRate = isBoosting ? 14 : (keyboardLike ? 16 : 14);
      const followT = 1 - Math.exp(-followRate * dt);
      this.position.x = THREE.MathUtils.lerp(this.position.x, this._targetLane.x, followT);
      this.position.y = THREE.MathUtils.lerp(this.position.y, this._targetLane.y, followT);

      const clampedX = THREE.MathUtils.clamp(this.position.x, -bx, bx);
      const clampedY = THREE.MathUtils.clamp(this.position.y, -by, by);
      this.position.x = clampedX;
      this.position.y = clampedY;
      this._targetLane.x = THREE.MathUtils.clamp(this._targetLane.x, -bx, bx);
      this._targetLane.y = THREE.MathUtils.clamp(this._targetLane.y, -by, by);

      if (dt > 0) {
        this.velocity.x = (this.position.x - prevX) / dt;
        this.velocity.y = (this.position.y - prevY) / dt;
      } else {
        this.velocity.x = 0;
        this.velocity.y = 0;
      }
      this.velocity.z = -currentSpeed;

      // Keep aim tethered to position for systems that sample it.
      this.aim.set(this.position.x, this.position.y);

      // Camera lead tracks stick input like SF64 cam eye/at offsets.
      const leadT = 1 - Math.exp(-(keyboardLike ? 11.5 : 7.5) * dt);
      this._cameraLead.x = THREE.MathUtils.lerp(this._cameraLead.x, steerX, leadT);
      this._cameraLead.y = THREE.MathUtils.lerp(this._cameraLead.y, steerY, leadT);

      // Apply rotation (frame-rate friendly damping).
      const tRot = 1 - Math.exp(-18 * dt);
      this.object3D.rotation.x = THREE.MathUtils.lerp(this.object3D.rotation.x, this.rotation.x, tRot);
      this.object3D.rotation.y = THREE.MathUtils.lerp(this.object3D.rotation.y, this.rotation.y, tRot);
      this.object3D.rotation.z = THREE.MathUtils.lerp(this.object3D.rotation.z, this.rotation.z, tRot);
    }
    
    // Update position
    this.object3D.position.copy(this.position);
    
    // Handle firing is above (tap/charge).
    
    // Handle bomb
    if (input.bomb) {
      this.dropBomb();
    }
    
    // Update trail
    this.updateTrail();
    
    // Update invulnerability
	    if (this.isInvulnerable) {
	      this.invulnerableTime -= deltaTime;
	      if (this.invulnerableTime <= 0) {
	        this.isInvulnerable = false;
	        this.applyShipOpacity(1);
	      } else {
	        // Flicker when invulnerable
	        this.applyShipOpacity(0.5 + Math.sin(Date.now() * 0.02) * 0.3);
	      }
	    }

	    this.updateShieldVisual(deltaTime);
	  }
  
  fire() {
    const now = Date.now() / 1000;
    if (now - this.lastFireTime < this.fireRate) return;
    
    this.lastFireTime = now;

    // Portrait reaction (best effort). UI layer will throttle/ignore if not supported.
    this.game?.uiManager?.setCharacterExpression?.(this.character, "angry", { ttlMs: 220 });
    
    // Create projectile(s) based on weapon level.
    // Favor Star Fox readability: originate slightly *in front* of the ship (down-lane).
    const projectilePositions = [];
    const shipPos = this.object3D.position.clone();
    const q = this.object3D.quaternion;
    const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(q).normalize();
    const right = new THREE.Vector3(1, 0, 0).applyQuaternion(q).normalize();

    const muzzleForward = 30;
    const wingForward = 26;
    const wingSeparation = 14;

    if (this.weaponLevel === 1) {
      projectilePositions.push(shipPos.clone().add(forward.clone().multiplyScalar(muzzleForward)));
    } else if (this.weaponLevel === 2) {
      projectilePositions.push(
        shipPos
          .clone()
          .add(forward.clone().multiplyScalar(wingForward))
          .add(right.clone().multiplyScalar(-wingSeparation))
      );
      projectilePositions.push(
        shipPos
          .clone()
          .add(forward.clone().multiplyScalar(wingForward))
          .add(right.clone().multiplyScalar(wingSeparation))
      );
    } else {
      projectilePositions.push(shipPos.clone().add(forward.clone().multiplyScalar(muzzleForward)));
      projectilePositions.push(
        shipPos
          .clone()
          .add(forward.clone().multiplyScalar(wingForward))
          .add(right.clone().multiplyScalar(-wingSeparation * 1.15))
      );
      projectilePositions.push(
        shipPos
          .clone()
          .add(forward.clone().multiplyScalar(wingForward))
          .add(right.clone().multiplyScalar(wingSeparation * 1.15))
      );
    }
    
    // Create projectiles
    const projectileDamage = this.getProjectileDamage();
    const projectileSpeed = this.getProjectileSpeed();
    const firedProjectiles = [];
    for (const pos of projectilePositions) {
      const aimSolution = this.game?.getAimSolution?.(pos) || null;
      const baseDirection =
        aimSolution?.direction ||
        this.game?.getAimDirection?.(pos) ||
        new THREE.Vector3(0, 0, -1);
      if (this.game.enemyManager) {
        const target = aimSolution?.target && !aimSolution.target.isDestroyed ? aimSolution.target : null;
        const createWithOptions = this.game.enemyManager.createProjectileWithOptions;
        const projectile = createWithOptions
          ? createWithOptions.call(
              this.game.enemyManager,
              pos,
              baseDirection,
              projectileSpeed,
              projectileDamage,
              true,
              {
                homingTarget: target,
                homingTurnRate: target ? 5.2 : 0,
                targeted: !!target,
              }
            )
          : this.game.enemyManager.createProjectile(
              pos,
              baseDirection,
              projectileSpeed,
              projectileDamage,
              true
            );
        this.game.scene.add(projectile.object3D);
        this.game.projectiles.push(projectile);
        firedProjectiles.push(projectile);
      }
    }
    
    // Play character-specific weapon sound
    if (this.game.soundManager.playCharacterSound) {
      this.game.soundManager.playCharacterSound(this.character, 'weapon');
    } else {
      this.game.soundManager.playSFX('laser');
    }

    return firedProjectiles[0] || null;
  }
  
  dropBomb() {
    if (this.bombs <= 0) return;
    
    this.bombs--;
    this.game.uiManager.updateBombs(this.bombs);
    if (this.onBomb) this.onBomb();
    
    // Create bomb explosion
    // EnemyManager owns the explosion object's scene attachment; it returns a wrapper, not a THREE.Object3D.
    this.game.enemyManager.createExplosion(
      this.object3D.position.clone(),
      50,
      100
    );
    
    // Damage all enemies in radius
    for (const enemy of this.game.enemies) {
      const distance = enemy.object3D.position.distanceTo(this.object3D.position);
      if (distance < 50) {
        enemy.takeDamage(100);
      }
    }
    
    this.game.soundManager.playSFX('bomb');
  }
  
  barrelRoll() {
    if (this.isBarrelRolling) return;

    this.isBarrelRolling = true;
    this.barrelRollTimer = this.barrelRollTime;
    this.isInvulnerable = true;
    this.invulnerableTime = this.barrelRollTime;
    this.game.soundManager?.playSFX('barrel_roll');
  }
  
  get isRolling() {
    return this.isBarrelRolling;
  }
  
  get mesh() {
    return this.object3D;
  }
  
  useBoost() {
    if (this.boost <= 0) return false;
    this.boost = Math.max(0, this.boost - 5);
    return true;
  }
  
  useBomb() {
    this.dropBomb();
  }
  
  takeDamage(amount) {
    if (this.isInvulnerable) return;

    amount = this.getIncomingDamage(amount);
    
    if (amount > 0 && this.onTakeDamage) this.onTakeDamage(amount);

    // Trigger "Peak" dialogue on damage
    if (this.game.dialogueManager) {
      this.game.dialogueManager.trigger(this.character, 'take_damage');
    }
    
    let remainingDamage = amount;

    
    // Shields absorb damage first
    if (this.shields > 0) {
      if (this.shields >= remainingDamage) {
        this.shields -= remainingDamage;
        remainingDamage = 0;
      } else {
        remainingDamage -= this.shields;
        this.shields = 0;
        // Shields depleted - brief invulnerability
        this.isInvulnerable = true;
        this.invulnerableTime = 1;
      }
      this.game.uiManager.updateShields(this.shields, this.maxShields);
    }
    
  // Remaining damage goes to hull
  if (remainingDamage > 0) {
    this.health = Math.max(0, this.health - remainingDamage);
    this.game.uiManager.updateHealth(this.health, this.maxHealth);

    if (this.health <= 0) {
      this.game.loseLife();
    }
  }
  }
  
  respawn() {
    this.position.set(0, 0, 0);
    this.velocity.set(0, 0, 0);
    this.rotation.set(0, 0, 0);
    this.object3D.position.copy(this.position);
    this.object3D.rotation.set(0, 0, 0);
    
    this.health = this.maxHealth;
    this.shields = this.maxShields;
    this.boost = this.maxBoost;
    
    this.isInvulnerable = true;
    this.invulnerableTime = 3;
    
    this.game.uiManager.updateHealth(this.health, this.maxHealth);
    this.game.uiManager.updateShields(this.shields, this.maxShields);
  }
  
  powerUp(type) {
    switch (type) {
      case 'laser':
        this.weaponLevel = Math.min(3, this.weaponLevel + 1);
        break;
      case 'shield':
        this.shields = Math.min(this.maxShields, this.shields + 25);
        this.game.uiManager.updateShields(this.shields, this.maxShields);
        break;
      case 'health':
        this.health = Math.min(this.maxHealth, this.health + 25);
        this.game.uiManager.updateHealth(this.health, this.maxHealth);
        break;
      case 'bomb':
        this.bombs = Math.min(this.maxBombs, this.bombs + 1);
        this.game.uiManager.updateBombs(this.bombs);
        break;
      case 'life':
        this.lives++;
        this.game.uiManager.updateLives(this.lives);
        break;
      case 'boost':
        this.boost = this.maxBoost;
        break;
    }
  }
  
  updateTrail() {
    if (!this.trail) return;
    
    const positions = this.trail.geometry.attributes.position.array;
    
    // Shift positions back
    for (let i = positions.length - 3; i >= 3; i -= 3) {
      positions[i] = positions[i - 3];
      positions[i + 1] = positions[i - 3 + 1];
      positions[i + 2] = positions[i - 3 + 2] + 2;
    }
    
    // Set front position
    positions[0] = this.object3D.position.x;
    positions[1] = this.object3D.position.y;
    positions[2] = this.object3D.position.z;
    
    this.trail.geometry.attributes.position.needsUpdate = true;
  }
  
  getBoundingBox() {
    if (this.collider) return new THREE.Box3().setFromObject(this.collider);
    return new THREE.Box3().setFromObject(this.object3D);
  }
  
  dispose() {
    this._disposed = true;
    this._shipLoadToken++;

    const disposeMaterial = (material) => {
      if (!material) return;
      if (Array.isArray(material)) {
        material.forEach(disposeMaterial);
        return;
      }
      for (const key of ["map", "emissiveMap", "normalMap", "roughnessMap", "metalnessMap", "aoMap", "alphaMap"]) {
        const tex = material[key];
        if (tex && typeof tex.dispose === "function") tex.dispose();
      }
      if (typeof material.dispose === "function") material.dispose();
    };

    const disposeObject = (obj) => {
      if (!obj) return;
      obj.traverse?.((child) => {
        if (child.geometry && typeof child.geometry.dispose === "function") child.geometry.dispose();
        if (child.material) disposeMaterial(child.material);
      });
    };

    if (this.ship) disposeObject(this.ship);
    if (this.trail) {
      this.trail.geometry.dispose();
      this.trail.material.dispose();
    }
    this.object3D.clear();
  }

  adaptLaneBoundsToViewport() {
    const base = this._baseLaneBounds;
    const bx = Number(base?.x) || 180;
    const by = Number(base?.y) || 115;

    const w = window.innerWidth || 1;
    const h = window.innerHeight || 1;
    const aspect = w / h;

    if (aspect < 1.2) {
      const scale = Math.max(0.35, aspect / (16 / 9));
      this.laneBounds.x = Math.round(bx * scale);
      this.laneBounds.y = by;
    } else {
      this.laneBounds.x = bx;
      this.laneBounds.y = by;
    }
  }
}
